import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import {
  Button,
  Grid,
  IconButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";
import { get } from "lodash";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import {
  useLazyMeterReadingQuery,
  usePostUploadBillAdjustmentFileMutation,
  usePostUploadMeterReadingsFileMutation,
} from "apis/MeterReading";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import Tooltip from "Components/Common/Tooltip";
import BillMeterReadingForm from "./BillMeterReadingForm";
import InputFieldText from "Components/Common/InputFieldText";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getOptionLabel, getOptionValue } from "common/enums";

import getFileExtension from "utils/getFileExtension";
import { useLazyDownloadExcelQuery } from "slices/api";
import { POWER_READ_ADMIN } from "common/constant";
import { useSelector } from "react-redux";

const validFileFormats = ["xls", "xlsx"];

const initialQuery = { meterNumber: "", consumerNumber: "" };

const getMeterDetailsValue = (data = [], key) =>
  data.map((obj) => get(obj, key)).join(" / ");

const billCycleStatusToDisableEdit = ["closed", "dispatched"];

const billCycleStatusToBillingAdjustment = ["Initiated", "In Progress"];

const BillMeterReading = ({ t }) => {
  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [uploadMeterReadingsFile, { isLoading: isMeterReadingFileUploading }] =
    usePostUploadMeterReadingsFileMutation();

  const [
    uploadBillingAdjustmentFile,
    { isLoading: isBillingAdjustFileUploading },
  ] = usePostUploadBillAdjustmentFileMutation();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const { roles } = useSelector((state) => state.Login);
  const isEditDisabled = roles.includes(POWER_READ_ADMIN);

  const [
    getMeterReadings,
    {
      data: {
        data: {
          utilityConsumerMeterReadings: meterReadings,
          billingCycleStatus,
        } = {},
      } = {},
      isLoading: isMeterReadingLoading,
      isFetching: isMeterReadingFetching,
    },
  ] = useLazyMeterReadingQuery();

  const [utilityConsumerId, setUtilityConsumerId] = useState("");
  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Electricity")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  useEffect(() => {
    if (updatedBillingCycleOptions.length) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      getMeterReadingsData(billingCycleId, initialQuery);
    }
  }, [updatedBillingCycleOptions]);

  const isBillingAdjustmentBtnDisabled = useMemo(
    () =>
      !!billingCycleStatus &&
      billCycleStatusToBillingAdjustment.includes(
        getOptionLabel("billingStatus", billingCycleStatus)
      ),
    [billingCycleStatus]
  );

  const isMeterReadingNonEditable = useMemo(
    () =>
      billingCycleStatus
        ? billCycleStatusToDisableEdit.includes(
            getOptionLabel("billingStatus", billingCycleStatus).toLowerCase()
          )
        : false,
    [billingCycleStatus]
  );

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Meter Number",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "meterNumber"),
      },
      {
        title: "Max Demand",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "maxDemand"),
      },
      {
        title: "Active Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "activeEnergy"),
      },
      {
        title: "Apparent Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "apparentEnergy"),
      },
      {
        title: "Reactive Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "reactiveEnergy"),
      },
      {
        title: "Peak Hour Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "peakHourEnergy"),
      },
      {
        title: "TOU Discount Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "touDiscountEnergy"),
      },
      {
        title: "Night Hour Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "nightHourEnergy"),
      },
      {
        title: "Export Energy",
        renderCell: ({ meterReadings }) =>
          getMeterDetailsValue(meterReadings, "exportEnergy"),
      },
      {
        title: "Meter Reading Date",
        renderCell: ({ meterReadings }) =>
          meterReadings
            .map(({ meterReadingDate }) =>
              convertUTCDateToLocalDate(meterReadingDate).format("DD-MM-YYYY")
            )
            .join(" / "),
      },
      ...(isEditDisabled
        ? []
        : [
            {
              title: "Actions",
              renderCell: ({ utilityConsumerId }) => (
                <IconButton
                  onClick={() => setUtilityConsumerId(utilityConsumerId)}
                  disabled={isMeterReadingNonEditable}
                >
                  <Tooltip title={t("Edit Meter Readings")}>
                    <EditIcon
                      fontSize="small"
                      color={isMeterReadingNonEditable ? "disabled" : "primary"}
                      className="cursor-pointer"
                    />
                  </Tooltip>
                </IconButton>
              ),
            },
          ]),
    ],
    [isMeterReadingNonEditable]
  );

  const handleUploadFile = async (
    selectedFile,
    isBillingAdjustment = false
  ) => {
    try {
      const formData = new FormData();
      const fileExtension = getFileExtension(selectedFile.name)?.toLowerCase();
      if (!validFileFormats.includes(fileExtension)) {
        toast.error(
          "Invalid file format, Please upload file with .xsl or .xlsx extension."
        );
      } else {
        formData.append("file", selectedFile);
        if (isBillingAdjustment) {
          await uploadBillingAdjustmentFile(billCycleId, formData);
        } else {
          await uploadMeterReadingsFile(formData);
        }
        setBillCycleId(updatedBillingCycleOptions[0]?.value ?? 0);
      }
    } catch (err) {}
  };

  const downloadBtnHandler = async () => {
    try {
      if (billCycleId) {
        await downloadExcel({
          endpoint: `/report/billing-cycle/${billCycleId}/meter-reading-excel`,
          sheetName: "Meter Readings",
          billCycleId,
        });
      }
    } catch (error) {}
  };

  const getMeterReadingsData = async (id, query) => {
    try {
      await getMeterReadings(id, query);
    } catch (error) {}
  };

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isMeterReadingLoading ||
          isMeterReadingFetching ||
          isMeterReadingFileUploading ||
          isBillingAdjustFileUploading ||
          isExcelFileDownloading
        }
      />
      {!!utilityConsumerId && !!billCycleId && (
        <BillMeterReadingForm
          utilityConsumerId={utilityConsumerId}
          handleClose={() => setUtilityConsumerId("")}
          billCycleId={billCycleId}
        />
      )}
      <BreadCrumb title={t("Meter Readings")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getMeterReadingsData(billCycleId, query);
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => setBillCycleId(value)}
              value={
                updatedBillingCycleOptions.find(
                  (status) => status.value === billCycleId
                ) ?? ""
              }
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
              options={updatedBillingCycleOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery({ ...query, consumerNumber: e.target.value });
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery({ ...query, meterNumber: e.target.value });
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>

          <Grid item lg={3}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                getMeterReadingsData(billCycleId, initialQuery);
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>

          <Grid container item md={7} rowGap={1} columnGap={1}>
            <Button
              variant="contained"
              sx={{ height: "40px", fontSize: "11px" }}
              size="small"
              disabled={isBillingAdjustmentBtnDisabled}
              component="label"
            >
              {t("Upload Adjustment")}
              <input
                style={{
                  display: "none",
                }}
                accept=".xls, .xlsx"
                type="file"
                onChange={async (e) => {
                  await handleUploadFile(e.target.files[0], true);
                  e.target.value = null;
                }}
              />
            </Button>
            <Button
              variant="contained"
              sx={{ height: "40px", fontSize: "11px" }}
              size="small"
              disabled={isMeterReadingNonEditable}
              component="label"
              className="ms-1"
            >
              {t("Upload Readings")}
              <input
                style={{
                  display: "none",
                }}
                accept=".xls, .xlsx"
                type="file"
                onChange={async (e) => {
                  await handleUploadFile(e.target.files[0]);
                  e.target.value = null;
                }}
              />
            </Button>

            {!!meterReadings?.length && (
              <Button
                sx={{ height: "40px", fontSize: "11px" }}
                variant="contained"
                size="small"
                color="primary"
                className="ms-1"
                onClick={downloadBtnHandler}
                disabled={!billCycleId}
              >
                {t("Download As xlsx")}
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <BaseTable
        data={meterReadings || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

BillMeterReading.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(BillMeterReading);
